import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import Layout from '../components/layout/Layout'
import './RegistrationPage.css'

const ERROR_MESSAGES: Record<string, { title: string; message: string }> = {
  'missing-token': {
    title: 'Missing Registration Token',
    message: 'The registration token was not provided.'
  },
  'product-mismatch': {
    title: 'Product Mismatch',
    message: 'This registration URL is not associated with your product subscription.'
  },
  'invalid-token': {
    title: 'Invalid Registration Token',
    message:
      'The registration token has expired or is invalid. Please return to AWS Marketplace and try subscribing again.'
  }
}

function RegistrationPage() {
  const [searchParams] = useSearchParams()
  const licenseKey = searchParams.get('key')
  const errorCode = searchParams.get('error')
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!licenseKey) return
    await navigator.clipboard.writeText(licenseKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!licenseKey && !errorCode) {
    return (
      <Layout>
        <div className='registration'>
          <div className='registration-error'>
            <h1>Registration</h1>
            <p>
              This page is used to complete your AWS Marketplace registration. If you arrived here directly, please
              start from <Link to='/'>the home page</Link>.
            </p>
          </div>
        </div>
      </Layout>
    )
  }

  if (errorCode) {
    const error = ERROR_MESSAGES[errorCode] ?? {
      title: 'Registration Error',
      message: 'An unexpected error occurred during registration.'
    }

    return (
      <Layout>
        <div className='registration'>
          <div className='registration-error'>
            <h1>{error.title}</h1>
            <p>{error.message}</p>
            <p>
              If this problem persists, please <a href='mailto:contact@cloudsoft.com.pl'>contact support</a>.
            </p>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className='registration'>
        <div className='registration-success'>
          <h1>Subscription Confirmed</h1>
          <p>Thank you for subscribing to Runless on AWS Marketplace. Your license key is shown below.</p>

          <div className='registration-key-box'>
            <code>{licenseKey}</code>
          </div>

          <button className='btn btn-primary registration-copy-btn' onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy to clipboard'}
          </button>

          <div className='registration-steps'>
            <strong>Next steps:</strong>
            <ol>
              <li>Copy the license key above.</li>
              <li>
                Open the Runless CloudFormation template in{' '}
                <a href='https://console.aws.amazon.com/cloudformation' target='_blank' rel='noopener noreferrer'>
                  AWS CloudFormation
                </a>
                .
              </li>
              <li>
                Paste the key into the <strong>LicenseKey</strong> parameter.
              </li>
              <li>Deploy — each region you deploy to counts as a separate licensed instance.</li>
            </ol>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default RegistrationPage
